(() => {
  function formatVehicleTag(tag) {
    const parts = tag.slice('nwan-fit-'.length).split('-').filter(Boolean);
    const year = /^\d{4}$/.test(parts[parts.length - 1] || '') ? parts.pop() : '';
    const uppercase = new Set(['bmw', 'mini', 'jcw', 'amg', 'gle', 'nx', 'gti', 'gtd', 'tsi', 'tdi', 'rs', 'st', 'vw']);
    const name = parts.map((part) => {
      if (uppercase.has(part) || /^[a-z]+\d+[a-z]*$/.test(part) || /^[a-z]$/.test(part)) return part.toUpperCase();
      return part.charAt(0).toUpperCase() + part.slice(1);
    }).join(' ')
      .replace(/^Mercedes Benz\b/, 'Mercedes-Benz')
      .replace(/\b([ABCES]) Class\b/g, '$1-Class')
      .replace(/\bS Line\b/g, 'S-Line')
      .replace(/\bR Design\b/g, 'R-Design');
    return `${name || 'Vehicle selection'}${year ? ` (${year})` : ''}`;
  }

  function initOptionSearch(root) {
    root.querySelectorAll('[data-filter-search]').forEach((input) => {
      if (input.dataset.searchInitialized) return;
      input.dataset.searchInitialized = 'true';
      const group = input.closest('.nwan-shop__filter-group');
      const options = [...group.querySelectorAll('[data-filter-option]')];
      const more = group.querySelector('[data-show-more]');
      let expanded = false;

      function updateOptions() {
        const query = input.value.trim().toLocaleLowerCase();
        let matching = 0;
        options.forEach((option) => {
          const match = option.textContent.toLocaleLowerCase().includes(query);
          if (match) matching += 1;
          option.hidden = !match || (!query && !expanded && matching > 8 && !option.querySelector('input').checked);
        });
        if (more) {
          more.hidden = Boolean(query) || options.length <= 8;
          more.textContent = expanded ? 'Show Less −' : 'Show More +';
        }
      }

      input.addEventListener('input', updateOptions);
      more?.addEventListener('click', () => { expanded = !expanded; updateOptions(); });
      updateOptions();
    });
  }

  function filterTokens(name, values) {
    return values.flatMap((value) => name === 'filter.p.product_type' || name === 'filter.p.tag' ? value.split(',') : [value]);
  }

  function selectionForOption(name, optionValue, selectedValues) {
    const selected = new Set(filterTokens(name, selectedValues));
    const matching = filterTokens(name, [optionValue]).filter((value) => selected.has(value));
    return { checked: matching.length > 0, value: matching.length ? matching.join(',') : optionValue };
  }

  function preserveUrlFilters(filters, params) {
    const controls = [...filters.querySelectorAll('input[name]')];
    const names = new Set([...params.keys()].filter((name) => name.startsWith('filter.') && name !== 'filter.v.availability'));
    names.forEach((name) => {
      const matchingControls = controls.filter((input) => input.name === name);
      if (!matchingControls.some((input) => input.type !== 'hidden')) {
        matchingControls.forEach((input) => input.remove());
        params.getAll(name).forEach((value) => {
          const hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = name;
          hidden.value = value;
          filters.append(hidden);
        });
        return;
      }
      const represented = new Set(filterTokens(name, controls.filter((input) => input.name === name && (input.type !== 'checkbox' || input.checked)).map((input) => input.value)));
      const missing = [...new Set(filterTokens(name, params.getAll(name)))].filter((value) => !represented.has(value));
      missing.forEach((value) => {
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = name;
        hidden.value = value;
        filters.append(hidden);
      });
    });
  }

  function initGroupClearLinks(filters) {
    filters.querySelectorAll('.nwan-shop__filter-group').forEach((group) => {
      const names = new Set([...group.querySelectorAll('input[name]')].map((input) => input.name));
      const url = new URL(window.location.href);
      const active = [...names].some((name) => url.searchParams.has(name));
      if (!active || group.querySelector('[data-clear-group]')) return;
      names.forEach((name) => url.searchParams.delete(name));
      url.searchParams.delete('page');
      const link = document.createElement('a');
      link.className = 'nwan-shop__show-more';
      link.dataset.clearGroup = 'true';
      link.style.display = 'block';
      link.href = url.pathname + url.search;
      const title = group.querySelector('summary').textContent.trim();
      link.textContent = `Clear ${title.toLocaleLowerCase()}`;
      group.querySelector('.nwan-shop__filter-options').append(link);
    });
  }

  function initVehicleRecovery(section, filters) {
    if (section.querySelector('.nwan-shop__fitment-summary')) return;
    const url = new URL(window.location.href);
    const tags = filterTokens('filter.p.tag', url.searchParams.getAll('filter.p.tag'));
    if (!tags.some((tag) => tag.startsWith('nwan-fit-'))) return;
    const remainingTags = tags.filter((tag) => !tag.startsWith('nwan-fit-'));
    url.searchParams.delete('filter.p.tag');
    url.searchParams.delete('page');
    remainingTags.forEach((tag) => url.searchParams.append('filter.p.tag', tag));
    const note = document.createElement('p');
    note.className = 'nwan-shop__vehicle-help';
    note.style.order = '4';
    note.append('Your vehicle selection is also applied. ');
    const link = document.createElement('a');
    link.href = url.pathname + url.search;
    link.textContent = 'Clear vehicle selection';
    note.append(link);
    filters.append(note);
  }

  async function loadIndependentFilters(filters, editedNames) {
    if (!filters?.dataset.independentFiltersUrl || !window.fetch) return;
    const status = document.createElement('p');
    status.className = 'nwan-shop__filter-empty';
    status.style.order = '-1';
    status.setAttribute('role', 'status');
    status.textContent = 'Loading filter options...';
    filters.prepend(status);
    filters.setAttribute('aria-busy', 'true');
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10000);

    try {
      const url = new URL(filters.dataset.independentFiltersUrl, window.location.origin);
      url.search = '';
      url.searchParams.set('section_id', 'nwan-shop-filter-options');
      const response = await fetch(url.toString(), { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error('Filter options unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const groups = [...html.querySelectorAll('[data-independent-filter-options] [data-independent-group]')];
      if (!groups.length) throw new Error('No filter options returned');

      const params = new URLSearchParams(window.location.search);
      groups.forEach((group) => {
        const names = new Set([...group.querySelectorAll('input[name]')].map((input) => input.name));
        const oldInputs = [...filters.querySelectorAll('input[name]')].filter((input) => names.has(input.name));
        const valuesFor = (name) => editedNames.has(name)
          ? oldInputs.filter((input) => input.name === name && (input.type !== 'checkbox' || input.checked)).map((input) => input.value)
          : params.getAll(name);

        names.forEach((name) => {
          const selected = valuesFor(name);
          const inputs = [...group.querySelectorAll('input[name]')].filter((input) => input.name === name);
          if (inputs[0]?.type !== 'checkbox') {
            inputs.forEach((input) => { input.value = selected[0] || ''; });
            return;
          }
          const covered = new Set();
          inputs.forEach((input) => {
            input.dataset.fullValue = input.value;
            const selection = selectionForOption(name, input.value, selected);
            input.checked = selection.checked;
            input.value = selection.value;
            if (input.checked) filterTokens(name, [input.value]).forEach((value) => covered.add(value));
          });
          // Keep incoming 3D category values even when Shopify omits them from its option list.
          [...new Set(filterTokens(name, selected))].filter((value) => !covered.has(value)).forEach((value) => {
            const label = document.createElement('label');
            label.className = 'nwan-shop__checkbox';
            label.dataset.filterOption = '';
            const input = document.createElement('input');
            input.type = 'checkbox';
            input.name = name;
            input.value = value;
            input.checked = true;
            const text = document.createElement('span');
            text.textContent = value;
            label.append(input, text);
            group.querySelector('[data-filter-values]').append(label);
          });
        });

        const oldGroups = new Set(oldInputs.map((input) => input.closest('.nwan-shop__filter-group')).filter(Boolean));
        oldInputs.filter((input) => input.type === 'hidden').forEach((input) => input.remove());
        oldGroups.forEach((oldGroup) => oldGroup.remove());
        filters.append(group);
      });
      filters.querySelectorAll('.nwan-shop__filter-empty').forEach((element) => { if (element !== status) element.remove(); });
      initOptionSearch(filters);
      initGroupClearLinks(filters);
      status.remove();
    } catch (_) {
      status.textContent = 'Some filter options could not be loaded. You can still change the options below or clear all filters.';
    } finally {
      window.clearTimeout(timeout);
      filters.removeAttribute('aria-busy');
    }
  }

  function initShop(section) {
    if (section.dataset.nwanShopInitialized) return;
    section.dataset.nwanShopInitialized = 'true';
    section.classList.add('nwan-shop--js');

    const layout = section.querySelector('.nwan-shop__layout');
    const mobileToggle = section.querySelector('.nwan-shop__mobile-filters');
    const filters = section.querySelector('.nwan-shop__filters');
    const sort = section.querySelector('.nwan-shop__sort select');
    const editedNames = new Set();

    section.querySelectorAll('[data-fitment-label]').forEach((label) => {
      const tag = label.dataset.fitmentLabel;
      if (tag?.startsWith('nwan-fit-') && tag !== 'nwan-fit-universal') {
        label.textContent = formatVehicleTag(tag);
      }
    });

    mobileToggle?.addEventListener('toggle', () => {
      layout.classList.toggle('is-filter-open', mobileToggle.open);
    });

    filters?.addEventListener('input', (event) => {
      if (event.target.name) editedNames.add(event.target.name);
    });
    filters?.addEventListener('change', (event) => {
      if (event.target.name) editedNames.add(event.target.name);
      if (event.target.matches('input[type="checkbox"], select')) {
        if (event.target.dataset.fullValue) event.target.value = event.target.dataset.fullValue;
        filters.requestSubmit();
      }
    });
    sort?.addEventListener('change', () => filters?.requestSubmit());

    initOptionSearch(section);
    if (filters) {
      const params = new URLSearchParams(window.location.search);
      filters.querySelectorAll('input[type="checkbox"]').forEach((input) => {
        if (input.name !== 'filter.p.product_type' && input.name !== 'filter.p.m.custom.vehicle_make') return;
        input.dataset.fullValue = input.value;
        const selection = selectionForOption(input.name, input.value, params.getAll(input.name));
        input.checked = selection.checked;
        input.value = selection.value;
      });
      filters.querySelectorAll('input[type="number"][name]').forEach((input) => {
        if (params.has(input.name)) input.value = params.get(input.name);
      });
      preserveUrlFilters(filters, params);
      initGroupClearLinks(filters);
      initVehicleRecovery(section, filters);
      if (filters.dataset.emptyResults === 'true' && mobileToggle) {
        mobileToggle.open = true;
        layout.classList.add('is-filter-open');
      }
      loadIndependentFilters(filters, editedNames);
    }

    const message = section.querySelector('[data-cart-message]');
    section.querySelectorAll('.nwan-shop__product-form').forEach((form) => {
      form.addEventListener('submit', async (event) => {
        if (!window.fetch || !window.FormData) return;
        event.preventDefault();
        const button = form.querySelector('button[type="submit"]');
        const label = button.textContent;
        button.disabled = true;
        button.textContent = 'Adding...';
        message.textContent = '';

        try {
          const response = await fetch(section.dataset.cartAddUrl, {
            method: 'POST',
            body: new FormData(form),
            headers: { Accept: 'application/json' }
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.description || 'Could not add this item.');

          button.textContent = 'Added!';
          message.append('Added to cart. ');
          const cartLink = document.createElement('a');
          cartLink.href = section.dataset.cartUrl.replace(/\.js$/, '');
          cartLink.textContent = 'View cart';
          message.append(cartLink);

          try {
            const cartResponse = await fetch(section.dataset.cartUrl);
            if (cartResponse.ok) {
              const cart = await cartResponse.json();
              document.querySelectorAll('.nwan-header__action[href*="/cart"] span').forEach((text) => {
                text.textContent = `Cart (${cart.item_count})`;
              });
            }
          } catch (_) { /* The item was added even if the count refresh fails. */ }
        } catch (error) {
          message.textContent = error.message || 'Could not add this item. Please try again.';
        } finally {
          window.setTimeout(() => {
            button.disabled = false;
            button.textContent = label;
          }, 1300);
        }
      });
    });
  }

  function initAll(root = document) {
    if (root.matches?.('.nwan-shop')) initShop(root);
    root.querySelectorAll('.nwan-shop').forEach(initShop);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
